import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle, Pencil, Save, ArrowRight,
  TrendingDown, Factory, Leaf, Zap, Server, Crown, Download
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reuse auto-mapping keywords (EN + DE + FR + ES + IT)
const KEYWORDS = {
  name: ['name', 'bezeichnung', 'description', 'product', 'material', 'firma', 'produktname', 'artikelbezeichnung', 'firmenname', 'company', 'kunde', 'customer', 'nom_produit', 'designation', 'produit', 'libelle', 'nombre_producto', 'producto', 'articulo', 'nome_prodotto', 'prodotto', 'denominazione', 'nom', 'nombre', 'nome', 'nome_produto', 'designacao', 'artigo', 'descricao', 'urun_adi', 'urun_ismi', 'malzeme', 'tanim', 'اسم_المنتج', 'المنتج', 'الصنف', '产品名称', '产品', '商品', '名称'],
  production: ['production', 'quantity', 'menge', 'volume', 'output', 'monatsmenge', 'production_qty', 'kwh', 'consumed', 'consumption', 'verbrauch', 'quantite', 'consommation', 'cantidad', 'consumo', 'quantita', 'quantidade', 'qtd', 'tuketim', 'miktar', 'hacim', 'الاستهلاك', 'الكمية', '消耗', '数量', '用量'],
};

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).filter(l => l.trim()).map(line => {
    const vals = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function autoDetectName(headers) {
  for (const h of headers) {
    const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (KEYWORDS.name.some(k => lower.includes(k))) return h;
  }
  return headers[0];
}

function autoDetectSystem(headers) {
  const joined = headers.join(' ').toLowerCase();
  if (/kundennummer|bezeichnung|belegnr|warengruppe|zolltarif|einheit|firmenname|branche/.test(joined)) return 'DATEV';
  return 'SAP';
}

export default function QuickPCFReport({ open, onClose }) {
  const [step, setStep] = useState('choose'); // choose | uploading | result | editing
  const [parsed, setParsed] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detectedSystem, setDetectedSystem] = useState('');
  const [aiUsed, setAiUsed] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => { setStep('choose'); setParsed(null); setResult(null); setLoading(false); setDetectedSystem(''); setAiUsed(false); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStep('uploading');
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const data = parseCSV(ev.target.result);
      setParsed(data);
      setDetectedSystem(autoDetectSystem(data.headers));

      // Auto-map: find name column
      const nameCol = autoDetectName(data.headers);
      const nameFound = KEYWORDS.name.some(k => nameCol.toLowerCase().replace(/[^a-z0-9]/g, '').includes(k));

      // Check mapping quality: did we find the name column via keywords?
      const totalHeaders = data.headers.length;
      const matchedHeaders = data.headers.filter(h => {
        const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
        return Object.values(KEYWORDS).flat().some(k => lower.includes(k));
      }).length;
      const matchRatio = totalHeaders > 0 ? matchedHeaders / totalHeaders : 0;

      let usedAI = false;

      // If match ratio < 70% or name not found, try AI fallback
      if (matchRatio < 0.7 || !nameFound) {
        try {
          const aiRes = await axios.post(`${API}/ai/detect-columns`, {
            headers: data.headers,
            sample_rows: data.rows.slice(0, 3),
            data_type: 'Materials'
          });
          if (aiRes.data.mapping && Object.keys(aiRes.data.mapping).length > 0) {
            const aiMapping = aiRes.data.mapping;
            // Use AI-detected name column if available
            const aiNameCol = aiMapping.name || aiMapping.product_name || nameCol;
            const mapped = data.rows.map(row => {
              const obj = { name: row[aiNameCol] || row[nameCol] || '' };
              data.headers.forEach(h => { if (h !== aiNameCol && h !== nameCol) obj[h] = row[h]; });
              return obj;
            });
            usedAI = true;
            setAiUsed(true);

            try {
              const res = await axios.post(`${API}/quick-pcf-report`, { rows: mapped });
              setResult(res.data);
              setStep('result');
            } catch (err) {
              setResult({ error: err.response?.data?.detail || 'Processing failed.' });
              setStep('result');
            }
            setLoading(false);
            return;
          }
        } catch {
          // AI failed, continue with keyword mapping
        }
      }

      // Standard keyword mapping
      const mapped = data.rows.map(row => {
        const obj = { name: row[nameCol] || '' };
        data.headers.forEach(h => { if (h !== nameCol) obj[h] = row[h]; });
        return obj;
      });

      try {
        const res = await axios.post(`${API}/quick-pcf-report`, { rows: mapped });
        setResult(res.data);
        setStep('result');
      } catch (err) {
        setResult({ error: err.response?.data?.detail || 'Processing failed.' });
        setStep('result');
      }
      setLoading(false);
    };
    reader.readAsText(file);
  };

  const handleSave = () => {
    onClose();
    navigate('/pcf');
  };

  const handleEdit = () => {
    onClose();
    navigate('/settings/import-data');
  };

  const totalEmissions = result?.total_emissions || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quick-pcf-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Leaf className="w-5 h-5 text-primary" /> New PCF Report
          </DialogTitle>
        </DialogHeader>

        {/* ===== Step: Choose ===== */}
        {step === 'choose' && (
          <div className="space-y-4 py-2" data-testid="quick-step-choose">
            <p className="text-sm text-muted-foreground">How do you want to provide your data?</p>

            <div
              className="flex items-center gap-4 p-4 rounded-xl border-2 border-dashed border-border hover:border-primary cursor-pointer transition-all group"
              onClick={() => fileRef.current?.click()}
              data-testid="quick-upload-option"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Upload className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sm">Upload CSV / Excel</p>
                <p className="text-xs text-muted-foreground">Drop a file and get instant PCF results. No manual mapping needed.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="quick-file-input" />
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl border border-border opacity-60 cursor-not-allowed" data-testid="quick-sap-option">
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                <Server className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sm flex items-center gap-1.5">
                  Connect SAP / DATEV
                  <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-amber-500/10 text-amber-600">
                    <Crown className="w-2.5 h-2.5" /> Enterprise
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">Import directly from your ERP system. Coming soon.</p>
              </div>
            </div>
          </div>
        )}

        {/* ===== Step: Uploading ===== */}
        {step === 'uploading' && (
          <div className="py-12 text-center" data-testid="quick-step-uploading">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
            <p className="font-heading font-semibold">Analyzing your data...</p>
            <p className="text-xs text-muted-foreground mt-1">Auto-mapping columns and calculating PCF</p>
          </div>
        )}

        {/* ===== Step: Result ===== */}
        {step === 'result' && result && !result.error && (
          <div className="space-y-4 py-2" data-testid="quick-step-result">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-3 text-center" data-testid="quick-total-products">
                <p className="text-2xl font-heading font-bold text-primary">{result.total_products}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Products</p>
              </Card>
              <Card className="p-3 text-center" data-testid="quick-total-emissions">
                <p className="text-2xl font-heading font-bold">{totalEmissions.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">tCO2 Total</p>
              </Card>
              <Card className="p-3 text-center" data-testid="quick-detected-system">
                <p className="text-lg font-heading font-bold text-accent">{result.language_detected?.toUpperCase() || detectedSystem}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Language / Source</p>
              </Card>
            </div>

            {/* Results Table */}
            <div className="rounded-xl border border-border overflow-hidden" data-testid="quick-results-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Production</TableHead>
                    <TableHead className="text-right">PCF (tCO2/t)</TableHead>
                    <TableHead className="text-right">Total tCO2</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.products?.slice(0, 8).map((p, i) => (
                    <TableRow key={i} data-testid={`quick-result-row-${i}`}>
                      <TableCell className="font-medium text-sm">{p.name}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{p.production?.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-semibold text-sm">{p.pcf}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{p.total_emissions?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Confirmation */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm" data-testid="quick-confirm-message">
              <p className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-primary" />
                <span className="font-medium">Does this look correct?</span>
                {aiUsed && <span className="cp-badge-indigo text-[10px] ml-2">AI-assisted mapping</span>}
              </p>
              <p className="text-xs text-muted-foreground mt-1 ml-5.5">
                Data {aiUsed ? 'AI-mapped' : 'auto-mapped'} from {parsed?.rows?.length || 0} rows. Products saved and PCF calculated with default emission factors.
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1 bg-primary hover:bg-primary/90" data-testid="quick-save-report-btn">
                <Save className="w-4 h-4 mr-2" /> Yes, View Full Report
              </Button>
              <Button variant="outline" onClick={handleEdit} className="flex-1" data-testid="quick-edit-data-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit Data
              </Button>
              {result?.report_id && (
                <Button variant="outline" asChild data-testid="quick-download-pdf-btn">
                  <a href={`${API}/export-pdf/${result.report_id}`} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {step === 'result' && result?.error && (
          <div className="py-8 text-center" data-testid="quick-step-error">
            <p className="text-red-500 text-sm">{result.error}</p>
            <Button variant="outline" onClick={reset} className="mt-4">Try Again</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
